'use client'

import Image from 'next/image'
import { useState } from 'react'

import { HeroBannerVideoModal } from './HeroBannerVideoModal'

export interface HeroBannerPlayBlockProps {
  videoSrc?: string
  watchVideoLabel?: string
  locale?: AppLocale
  /** When true, block is in-flow (e.g. centered in stacked mobile red panel). */
  inline?: boolean
}

export function HeroBannerPlayBlock({
  videoSrc,
  watchVideoLabel = 'WATCH VIDEO',
  locale = 'en',
  inline = false,
}: HeroBannerPlayBlockProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const isAr = locale === 'ar'
  const labelClass = isAr ? 'font-arabic normal-case' : 'font-bebas-neue uppercase'

  return (
    <>
      <div className="mb-10 md:mb-0">
        {/* Main container: WATCH VIDEO text, curved arrow, play button */}
        <div
          className={
            inline
              ? 'group flex w-full flex-row flex-wrap items-center justify-center gap-2 py-4 sm:gap-3 sm:py-6'
              : 'hero-banner-play-block-desktop group absolute right-[18%] top-[10%] z-20 flex flex-row items-center gap-0 -translate-x-[80px] -translate-y-[30px]'
          }
        >
          {/* Mobile: WATCH VIDEO + arrow to left of play button, shifted 10px up */}
          {inline ? (
            <>
              <span
                className={`-mt-[50px] shrink-0 text-[10px] max-[400px]:text-[9px] font-medium leading-tight tracking-normal text-white sm:text-xs transition-transform duration-300 ease-in-out group-hover:scale-110 cursor-pointer ${labelClass}`.trim()}
              >
                {watchVideoLabel}
              </span>
              <div className="-mt-[50px] mx-0.5 max-[400px]:mx-0 flex shrink-0 items-center sm:mx-2">
                <Image
                  src="/icon/arrow.svg"
                  alt="Directional arrow"
                  width={70}
                  height={27}
                  className="h-4 max-[400px]:h-3.5 w-10 max-[400px]:w-9 object-contain sm:h-6 sm:w-14"
                  aria-hidden="true"
                  priority
                />
              </div>
              <div className="flex shrink-0">
                <button
                  type="button"
                  aria-label={watchVideoLabel}
                  onClick={() => setModalOpen(true)}
                  className="flex h-20 w-20 min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-white transition-all duration-300 ease-in-out hover:opacity-90 group-hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#E00000] sm:h-24 sm:w-24"
                >
                  <svg
                    className="ml-0.5 h-6 w-6 max-[400px]:h-5 max-[400px]:w-5 text-[#E4002B] transition-transform duration-300 ease-in-out group-hover:scale-110 sm:h-7 sm:w-7"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <>
              <span
                className={`shrink-0 text-xs font-medium leading-tight tracking-normal text-white sm:text-sm -mt-12 transition-transform duration-300 ease-in-out group-hover:scale-110 cursor-pointer ${labelClass}`.trim()}
              >
                {watchVideoLabel}
              </span>
              <div className="mx-2 flex shrink-0 items-center sm:mx-3 -mt-2.5">
                <Image
                  src="/icon/arrow.svg"
                  alt="Directional arrow"
                  width={70}
                  height={27}
                  className="h-8 w-[70px] object-contain sm:h-9 sm:w-[82px] md:h-10 md:w-[92px]"
                  aria-hidden="true"
                  priority
                />
              </div>
            </>
          )}

          {/* Play button – desktop only (mobile has its own in the inline block above) */}
          {!inline && (
            <div className="ml-2 mt-[19.5px] shrink-0 pr-0">
              <div className="mt-0">
                <button
                  type="button"
                  aria-label={watchVideoLabel}
                  onClick={() => setModalOpen(true)}
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-white transition-all duration-300 ease-in-out hover:opacity-90 group-hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent sm:h-28 sm:w-28 md:h-36 md:w-36 lg:h-[150px] lg:w-[150px] lg:rounded-[160px] mt-24"
                >
                  <svg
                    className="ml-0.5 h-8 w-8 text-[#DC2626] transition-transform duration-300 ease-in-out group-hover:scale-110 sm:h-10 sm:w-10 md:h-12 md:w-12 lg:ml-1 lg:h-14 lg:w-14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <HeroBannerVideoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        videoSrc={videoSrc}
      />
    </>
  )
}
